import { askConfirm } from '../lib/askConfirm';
import { useRef, useState } from 'react';
import { Sprout, FlaskConical } from 'lucide-react';
import { openGarden } from './GardenCalc';
import { openAgents } from './AgentDb';
import UnitInput from './UnitInput';
import { len, depU } from '../lib/units';
import { Shovel, Spline, Check, Undo2, X, Trash2, MapPinPlus, Crosshair, Camera, Video, LockOpen, Link2, LocateFixed, Filter, ScanSearch } from 'lucide-react';
import { PIPE_TYPES, FITTING_TYPES, FIT_STATES, pathLength, pipeLatLngs, compressImage, uid } from '../lib/model';
import { videoThumb } from '../lib/media';
import MediaThumb from './MediaThumb';
import PitDrawBar from './PitDrawBar';
import { Navigation } from 'lucide-react';
import { routeLink } from '../lib/route';
import PitList from './PitList';
import MaterialList from './MaterialList';
import { CatalogSelect, DnSelect } from './CatalogPicker';
import useCatalog from '../hooks/useCatalog';
import { VoiceRecorder, MemoList } from './VoiceMemo';
import { useFeatures, feat } from '../lib/features';
import { ml, getMode, allowedTypes, fitTypes } from '../lib/profiles';
import { gardenItems, noDn, bySector } from '../lib/catalog';

const TypeSelect = ({ t, types, prefix, value, onChange, testid }) => (
  <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}>
    {Object.keys(types).filter((k) => k !== 'leak' || k === value).filter((k) => prefix !== 'fit_' || !fitTypes() || fitTypes().includes(k) || k === value).filter((k) => prefix !== 'type_' || !allowedTypes() || allowedTypes().includes(k) || k === value).map((k) => <option key={k} value={k}>{t[prefix + k]}</option>)}
  </select>
);

export const FitSymbol = ({ t, type }) => (
  <span className={`fit-sym fit-${type}`} style={{ background: FITTING_TYPES[type] }}>{t['fab_' + type]}</span>
);

const place = (mode) => window.dispatchEvent(new CustomEvent('giss-place', { detail: mode }));

export function DrawBar({ t, net, pits }) {
  const d = net.draft;
  const { items } = useCatalog();
  const ft = useFeatures();
  if (d.kind === 'pit') return <PitDrawBar t={t} net={net} pits={pits} />;
  const isPipe = d.kind === 'pipe';
  return (
    <div className="draw-bar" data-testid="pipe-draw-bar">
      <p>{isPipe ? t.drawHint : t.fitHint}</p>
      <div className="row">
        {d.catId && <CatalogSelect t={t} kind={isPipe ? 'pipe' : 'point'} testid="draft-catalog-select" value={d.catId} onChange={(it) => it && net.startItem(it, d.dn, d)} />}
        {d.catId && isPipe && !noDn(items.find((x) => x.id === d.catId) || {}) && <DnSelect t={t} testid="draft-dn-select" value={d.dn} onChange={(dn) => net.startItem(items.find((x) => x.id === d.catId), dn, d)} />}
        {!d.catId && (isPipe
          ? <TypeSelect t={t} types={PIPE_TYPES} prefix="type_" testid="draft-type-select" value={d.type} onChange={(type) => net.setDraft({ ...d, type })} />
          : <TypeSelect t={t} types={FITTING_TYPES} prefix="fit_" testid="draft-fitting-type-select" value={d.type} onChange={(type) => net.setDraft({ ...d, type })} />)}
        {isPipe && <span className="draw-count" data-testid="draft-point-count">{d.points.length} {t.drawPoints}</span>}
        <button className="btn btn-primary" data-testid="add-crosshair-point-btn" onClick={net.addAtCrosshair}><Crosshair size={16} /> {isPipe ? t.btnCrosshair : `🎯 ${t.plcCross}`}</button>
        {!isPipe && <button className="btn btn-outline" data-testid="place-dock-btn" onClick={() => place('dock')}>🔗 {t.plcDock}</button>}
        {!isPipe && ft.survey && <button className="btn btn-outline" data-testid="place-survey-btn" onClick={() => place('survey')}>📐 {t.plcSurvey}</button>}
      </div>
      {isPipe && (
        <div className="row">
          <button className="btn btn-outline btn-sm" data-testid="pipe-mark-gg-btn" disabled={!d.points.length} onClick={() => place('gg')}><Crosshair size={14} /> 🚧 {t.bndGGx}</button>
          <button className="btn btn-outline btn-sm" data-testid="pipe-mark-gk-btn" disabled={!d.points.length} onClick={() => place('gk')}><Crosshair size={14} /> 🧱 {t.bndGKx}</button>
          {d.private && <span className="priv-tag" data-testid="draft-private-tag">--- {t.privPipe}</span>}
        </div>
      )}
      <div className="row">
        {isPipe ? (
          <>
            <button className="btn btn-green" data-testid="pipe-finish-btn" disabled={d.points.length < 2} onClick={net.finishPipe}><Check size={16} /> {t.btnFinish}</button>
            <button className="btn btn-outline" data-testid="pipe-undo-btn" disabled={!d.points.length} onClick={() => net.setDraft({ ...d, points: d.points.slice(0, -1) })}><Undo2 size={16} /> {t.btnUndo}</button>
            <button className="btn btn-outline" data-testid="pipe-cancel-btn" onClick={net.cancelDraft}><X size={16} /> {t.btnCancel}</button>
          </>
        ) : (
          <button className="btn btn-green" data-testid="fitting-done-btn" onClick={() => net.setDraft(null)}><Check size={16} /> {t.btnDone}</button>
        )}
      </div>
    </div>
  );
}

function endLabel(t, pt, fittings) {
  if (Array.isArray(pt)) return t.freePoint;
  return fittings.find((f) => f.id === pt.fid)?.name || t.freePoint;
}

function PipeCard({ t, p, i, net, fittings }) {
  const pts = pipeLatLngs(p, fittings);
  const links = p.points.filter((x) => x.fid).length;
  return (
    <div className="pipe-card-wrap" data-testid={`pipe-card-${i}`}>
      <div className="pipe-card">
        <span className="pipe-swatch" style={{ background: p.color || PIPE_TYPES[p.type] }} />
        <input type="text" data-testid={`pipe-name-input-${i}`} placeholder={t.pipeName} value={p.name} onChange={(e) => net.updatePipe(p.id, { name: e.target.value })} />
        <TypeSelect t={t} types={PIPE_TYPES} prefix="type_" testid={`pipe-type-select-${i}`} value={p.type} onChange={(type) => net.updatePipe(p.id, { type })} />
        <input type="text" data-testid={`pipe-dn-input-${i}`} placeholder={t.pipeDn} value={p.dn || ''} onChange={(e) => net.updatePipe(p.id, { dn: e.target.value })} />
        <label className="pipe-depth" title={t.dpTitle} data-unit={depU()}>{t.dpShort}<UnitInput kind="dep" testid={`pipe-depth-input-${i}`} value={p.depth} onChange={(v) => net.updatePipe(p.id, { depth: v === '' ? null : +v })} /></label>
        <span className="pipe-len" data-testid={`pipe-length-${i}`}>{len(pathLength(pts))}</span>
        <button className="icon-btn danger" data-testid={`pipe-delete-btn-${i}`} onClick={() => net.deletePipe(p.id)}><Trash2 size={15} /></button>
      </div>
      <div className="pipe-route" data-testid={`pipe-route-${i}`}>
        {t.pipeFrom} <b>{endLabel(t, p.points[0], fittings)}</b> {t.pipeTo} <b>{endLabel(t, p.points[p.points.length - 1], fittings)}</b>
        {links > 0 && <span className="link-tag"><Link2 size={11} /> {links}× {t.connected}</span>}
      </div>
    </div>
  );
}

function FittingCard({ t, f, i, net, onDictate, onLocate }) {
  const camRef = useRef(null);
  const vidRef = useRef(null);
  const media = f.media || [];
  const addMedia = async (e, kind) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const item = kind === 'video'
      ? { id: uid('ph'), kind: 'video', blob: file, mime: file.type, caption: '', ...(await videoThumb(file)) }
      : { id: uid('ph'), kind: 'photo', caption: '', ...(await compressImage(file)) };
    net.updateFitting(f.id, (x) => ({ media: [...(x.media || []), item] }));
  };
  return (
    <div className="pipe-card-wrap fit-card-wrap" data-testid={`fitting-card-${i}`}>
      <div className="pipe-card fit-card">
        <FitSymbol t={t} type={f.type} />
        <input type="text" data-testid={`fitting-name-input-${i}`} value={f.name} onChange={(e) => net.updateFitting(f.id, { name: e.target.value })} />
        <TypeSelect t={t} types={FITTING_TYPES} prefix="fit_" testid={`fitting-type-select-${i}`} value={f.type} onChange={(type) => net.updateFitting(f.id, { type })} />
        <select data-testid={`fitting-state-select-${i}`} value={f.state || 'none'} onChange={(e) => net.updateFitting(f.id, { state: e.target.value })} style={{ borderLeft: `5px solid ${FIT_STATES[f.state || 'none'] || '#d9d4c8'}` }}>
          {Object.keys(FIT_STATES).map((k) => <option key={k} value={k}>{t['st_' + k]}</option>)}
        </select>
        <button className="btn btn-outline btn-sm" data-testid={`fitting-locate-btn-${i}`} onClick={() => onLocate([[f.lat, f.lon]])} title={`${f.lat.toFixed(6)}, ${f.lon.toFixed(6)}`}><LocateFixed size={14} /> {t.locate}</button>
        <a className="btn btn-green btn-sm" data-testid={`fitting-navigate-btn-${i}`} href={routeLink(f)} target="_blank" rel="noreferrer" title={t.alNavHere}><Navigation size={14} /></a>
        <button className="icon-btn danger" data-testid={`fitting-delete-btn-${i}`} onClick={() => net.deleteFitting(f.id)}><Trash2 size={15} /></button>
      </div>
      <div className="fit-extra">
        <input type="text" data-testid={`fitting-note-input-${i}`} placeholder={t.fitNote} value={f.note || ''} onChange={(e) => net.updateFitting(f.id, { note: e.target.value })} />
        <input type="text" data-testid={`fitting-serial-input-${i}`} placeholder={t.serial} value={f.serial || ''} onChange={(e) => net.updateFitting(f.id, { serial: e.target.value })} />
        <button className="btn btn-outline btn-sm" data-testid={`fitting-photo-btn-${i}`} onClick={() => camRef.current.click()}><Camera size={15} /> {t.btnPhoto}</button>
        <button className="btn btn-outline btn-sm" data-testid={`fitting-video-btn-${i}`} onClick={() => vidRef.current.click()}><Video size={15} /> {t.video}</button>
        <VoiceRecorder t={t} compact testid={`fitting-memo-btn-${i}`} onDone={(m) => { net.updateFitting(f.id, (x) => ({ memos: [...(x.memos || []), m] })); onDictate(`${f.name}: `, m.transcript); }} />
        <input ref={camRef} type="file" accept="image/*" capture="environment" hidden data-testid={`fitting-photo-input-${i}`} onChange={(e) => addMedia(e, 'photo')} />
        <input ref={vidRef} type="file" accept="video/*" capture="environment" hidden data-testid={`fitting-video-input-${i}`} onChange={(e) => addMedia(e, 'video')} />
      </div>
      {media.length > 0 && (
        <div className="fit-media">
          {media.map((m, j) => (
            <div key={m.id} className="fit-media-item" data-testid={`fitting-${i}-media-${j}`}>
              <MediaThumb item={m} className="fit-thumb" />
              <button className="icon-btn danger mini-del" data-testid={`fitting-${i}-media-delete-${j}`} onClick={async () => (await askConfirm(t.confirmDelPhoto)) && net.updateFitting(f.id, (x) => ({ media: x.media.filter((y) => y.id !== m.id) }))}><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      )}
      <MemoList t={t} memos={f.memos} prefix={`fitting-${i}`} onDelete={(mid) => net.updateFitting(f.id, (x) => ({ memos: x.memos.filter((y) => y.id !== mid) }))} />
    </div>
  );
}

function StateFilter({ t, fittings, filter, setFilter, onLocate }) {
  const counts = Object.keys(FIT_STATES).map((k) => [k, fittings.filter((f) => (f.state || 'none') === k).length]).filter(([, n]) => n);
  const toggle = (k) => setFilter(filter.includes(k) ? filter.filter((x) => x !== k) : [...filter, k]);
  const hits = fittings.filter((f) => filter.includes(f.state || 'none'));
  return (
    <div className="state-filter" data-testid="state-filter">
      <span className="inv-label"><Filter size={13} /> {t.filterTitle}:</span>
      <button className={`tool ${!filter.length ? 'on' : ''}`} data-testid="filter-all-btn" onClick={() => setFilter([])}>{t.filterAll} <b className="inv-n">{fittings.length}</b></button>
      {counts.map(([k, n]) => (
        <button key={k} className={`tool ${filter.includes(k) ? 'on' : ''}`} data-testid={`filter-${k}-btn`} onClick={() => toggle(k)}>
          <i className="inv-dot" style={{ background: FIT_STATES[k] || '#d9d4c8' }} /> {t['st_' + k]} <b className="inv-n">{n}</b>
        </button>
      ))}
      {hits.length > 0 && <button className="btn btn-dark btn-sm" data-testid="filter-show-map-btn" onClick={() => onLocate(hits.map((f) => [f.lat, f.lon]))}><ScanSearch size={14} /> {t.showOnMap}</button>}
    </div>
  );
}

// large networks: the list shows a page of cards (+ search), the map stays complete
const PAGE = 30;
const More = ({ t, n, total, set, testid }) => (total > n ? <button className="btn btn-outline btn-sm list-more" data-testid={testid} onClick={() => set(n + 50)}>{t.listMore.replace('{n}', Math.min(50, total - n)).replace('{t}', total - n)}</button> : null);

export default function PipePanel({ t, project, net, pits, fs, onDictate, fitFilter, setFitFilter, onLocate }) {
  const fittings = project.fittings || [];
  const [q, setQ] = useState(''), [pN, setPN] = useState(PAGE), [fN, setFN] = useState(PAGE);
  const ql = q.trim().toLowerCase(), hit = (o) => !ql || `${o.name || ''} ${o.serial || ''}`.toLowerCase().includes(ql);
  const idx = new Map(fittings.map((f, i) => [f.id, i]));
  const shown = (fitFilter.length ? fittings.filter((f) => fitFilter.includes(f.state || 'none')) : fittings).filter(hit);
  const pipesShown = project.pipes.map((p, i) => [p, i]).filter(([p]) => hit(p));
  const big = project.pipes.length + fittings.length > PAGE * 2;
  // branch sector first, so 'draw' / 'place' start with an item of this branch (e.g. district heating)
  const cat = useCatalog(), items = bySector(cat.items, cat.sector);
  const ft = useFeatures();
  const gd = getMode() === 'garden';
  const total = project.pipes.reduce((s, p) => s + pathLength(pipeLatLngs(p, fittings)), 0);
  return (
    <div className="pipe-panel" data-testid="pipe-panel">
      {net.draft ? (!fs && <DrawBar t={t} net={net} pits={pits} />) : (
        <div className="row">
          {ft.network && <button className="btn btn-dark" data-testid="draw-pipe-btn" onClick={() => (gd ? net.startItem(gardenItems(items).find((x) => x.kind === 'pipe') || items.find((x) => x.kind === 'pipe'), 32) : net.startItem(items.find((x) => x.kind === 'pipe'), 100))}><Spline size={16} /> {ml(t, 'btnDrawPipe')}</button>}
          {ft.fittings && <button className="btn btn-outline" data-testid="add-fitting-btn" onClick={() => net.startItem((gd && gardenItems(items).find((x) => x.kind !== 'pipe')) || items.find((x) => x.kind !== 'pipe'))}><MapPinPlus size={16} /> {ml(t, 'btnAddFitting')}</button>}
          {ft.pits && <button className="btn btn-outline" data-testid="add-pit-btn" onClick={() => pits.start('rect')}><Shovel size={16} /> {t.pitTool}</button>}
          {gd && <button className="btn btn-green" data-testid="garden-rows-btn" onClick={() => openGarden(null, 'row')}><Sprout size={16} /> {t.gdRows}</button>}
          {gd && <button className="btn btn-green" data-testid="garden-land-btn" onClick={() => openGarden(null, 'land')}><Sprout size={16} /> {t.gdLand}</button>}
          {gd && <button className="btn btn-outline" data-testid="garden-agents-btn" onClick={openAgents}><FlaskConical size={16} /> {t.agTitle}</button>}
          {net.netUnlocked
            ? <button className="btn btn-accent" data-testid="net-confirm-btn" onClick={() => net.setNetLocked(true)}><Check size={16} /> {t.btnNetConfirm}</button>
            : <button className="btn btn-outline" data-testid="net-unlock-btn" disabled={!project.pipes.length && !fittings.length} onClick={() => net.setNetLocked(false)}><LockOpen size={16} /> {t.btnNetUnlock}</button>}
        </div>
      )}
      {net.netUnlocked && !net.draft && <div className="draw-bar net-hint" data-testid="net-edit-hint"><p>{t.netEditHint}</p></div>}
      {ft.network && !project.pipes.length && !net.draft && <div className="empty" data-testid="pipes-empty">{ml(t, 'noPipes')}</div>}
      {big && <input className="list-search" data-testid="pipe-panel-search" value={q} placeholder={t.listSearch} onChange={(e) => { setQ(e.target.value); setPN(PAGE); setFN(PAGE); }} />}
      {ft.network && pipesShown.slice(0, pN).map(([p, i]) => <PipeCard key={p.id} t={t} p={p} i={i} net={net} fittings={fittings} />)}
      {ft.network && <More t={t} n={pN} total={pipesShown.length} set={setPN} testid="pipe-list-more" />}
      {ft.network && project.pipes.length > 0 && <div className="pipe-total" data-testid="pipe-total-length">{t.pipeTotal}: {len(total)}</div>}
      {ft.fittings && <div className="sub-title">{ml(t, 'fittingsTitle')} <em className="count" data-testid="fitting-count">{fittings.length}</em></div>}
      {ft.fittings && !fittings.length && <div className="empty" data-testid="fittings-empty">{ml(t, 'noFittings')}</div>}
      {ft.fittings && fittings.length > 0 && <StateFilter t={t} fittings={fittings} filter={fitFilter} setFilter={setFitFilter} onLocate={onLocate} />}
      {ft.pits && <PitList t={t} project={project} pits={pits} onLocate={onLocate} />}
      {feat('costs') && <MaterialList t={t} project={project} reportId={net.repId} />}
      {ft.fittings && shown.slice(0, fN).map((f) => <FittingCard key={f.id} t={t} f={f} i={idx.get(f.id)} net={net} onDictate={onDictate} onLocate={onLocate} />)}
      {ft.fittings && <More t={t} n={fN} total={shown.length} set={setFN} testid="fitting-list-more" />}
    </div>
  );
}
